import type { LabelLayoutTemplate } from './labelLayoutEngine';

/** Physical vessel that holds the product (not workspace “Packages” bundles). */
export type ProductContainerType =
  | 'CLEAR_BAG'
  | 'STANDUP_POUCH'
  | 'FOIL_POUCH'
  | 'PLASTIC_JAR'
  | 'GLASS_JAR'
  | 'BOTTLE'
  | 'TUB'
  | 'BOX'
  | 'PREPRINTED_BAG'
  | 'TIN_CAN'
  | 'MESH_SACK'
  | 'CLAMSHELL'
  | 'CANISTER';

export type LabelPlacement =
  | 'FRONT_STICKER'
  | 'WRAP_AROUND'
  | 'TOP_LID'
  | 'BACK_PANEL'
  | 'FULL_SURFACE'
  | 'HANG_TAG';

export type LabelFamily =
  | 'WRAP_PANEL'
  | 'RECT_STICKER'
  | 'VERTICAL_TAG'
  | 'NUTRITION_PANEL'
  | 'SLEEVE'
  | 'CLASSIC_TAG';

export type ProductContainerDesign = {
  containerType: ProductContainerType;
  labelPlacement: LabelPlacement;
  labelFamily: LabelFamily;
  containerColor: string;
  containerAccentColor: string;
  lidColor: string;
  fillOpacity: number;
};

export const DEFAULT_CONTAINER_DESIGN: ProductContainerDesign = {
  containerType: 'CLEAR_BAG',
  labelPlacement: 'FRONT_STICKER',
  labelFamily: 'RECT_STICKER',
  containerColor: '#e2e8f0',
  containerAccentColor: '#64748b',
  lidColor: '#ffffff',
  fillOpacity: 0.55,
};

export function mergeContainerDesign(
  raw?: Partial<ProductContainerDesign> | null,
): ProductContainerDesign {
  return { ...DEFAULT_CONTAINER_DESIGN, ...(raw || {}) };
}

export const CONTAINER_TYPE_OPTIONS: Array<{
  value: ProductContainerType;
  name: string;
  description: string;
}> = [
  { value: 'CLEAR_BAG', name: 'Clear bag', description: 'Transparent poly bag with front sticker' },
  { value: 'STANDUP_POUCH', name: 'Stand-up pouch', description: 'Zip pouch with gusset base' },
  { value: 'FOIL_POUCH', name: 'Foil pouch', description: 'Metallic heat-sealed pouch' },
  { value: 'PLASTIC_JAR', name: 'Plastic jar', description: 'Screw-cap jar with wrap label' },
  { value: 'GLASS_JAR', name: 'Glass jar', description: 'Glass jar with sleeve label' },
  { value: 'BOTTLE', name: 'Bottle', description: 'Narrow neck bottle with wrap label' },
  { value: 'TUB', name: 'Tub', description: 'Wide tub or deli container' },
  { value: 'BOX', name: 'Box / carton', description: 'Folding carton with panel labels' },
  { value: 'PREPRINTED_BAG', name: 'Pre-printed bag', description: 'Fully printed retail bag' },
  { value: 'TIN_CAN', name: 'Tin can', description: 'Metal tin with wrap or lid label' },
  { value: 'MESH_SACK', name: 'Mesh / burlap sack', description: 'Produce sack with tag or stamp' },
  { value: 'CLAMSHELL', name: 'Clamshell tray', description: 'Clear hinged tray for produce' },
  { value: 'CANISTER', name: 'Canister', description: 'Tall cylinder tin or cardboard canister' },
];

export const LABEL_PLACEMENT_OPTIONS: Array<{
  value: LabelPlacement;
  name: string;
}> = [
  { value: 'FRONT_STICKER', name: 'Front sticker' },
  { value: 'WRAP_AROUND', name: 'Wrap-around' },
  { value: 'TOP_LID', name: 'Top / lid' },
  { value: 'BACK_PANEL', name: 'Back panel' },
  { value: 'FULL_SURFACE', name: 'Full surface print' },
  { value: 'HANG_TAG', name: 'Hang tag' },
];

export const LABEL_FAMILY_OPTIONS: Array<{
  value: LabelFamily;
  name: string;
  layoutTemplate: LabelLayoutTemplate;
  description: string;
}> = [
  { value: 'RECT_STICKER', name: 'Rectangle sticker', layoutTemplate: 'SQUARE_CENTER', description: 'Front panel sticker on bags & pouches' },
  { value: 'VERTICAL_TAG', name: 'Vertical tag', layoutTemplate: 'VERTICAL_STACK', description: 'Tall narrow tag (spice bags)' },
  { value: 'WRAP_PANEL', name: 'Horizontal wrap', layoutTemplate: 'HORIZONTAL_SPLIT', description: 'Split brand and details' },
  { value: 'NUTRITION_PANEL', name: 'Compliance panel', layoutTemplate: 'COMPLIANCE_GRID', description: 'Regulatory fields prioritized' },
  { value: 'SLEEVE', name: 'Cylinder sleeve', layoutTemplate: 'HORIZONTAL_SPLIT', description: 'Wrap on jars and bottles' },
  { value: 'CLASSIC_TAG', name: 'Shelf tag', layoutTemplate: 'SQUARE_CENTER', description: 'Small price tag (not on product)' },
];

export type PackagingPresentationPreset = {
  id: string;
  name: string;
  description: string;
  container: ProductContainerDesign;
  widthIn: number;
  heightIn: number;
  layoutTemplate: LabelLayoutTemplate;
};

export const PACKAGING_PRESENTATION_PRESETS: PackagingPresentationPreset[] = [
  {
    id: 'clear-bag-sticker',
    name: 'Clear bag · sticker',
    description: 'Spices in clear bag with rectangular front label',
    container: {
      containerType: 'CLEAR_BAG',
      labelPlacement: 'FRONT_STICKER',
      labelFamily: 'RECT_STICKER',
      containerColor: '#dbeafe',
      containerAccentColor: '#3b82f6',
      lidColor: '#ffffff',
      fillOpacity: 0.45,
    },
    widthIn: 2.5,
    heightIn: 3.5,
    layoutTemplate: 'SQUARE_CENTER',
  },
  {
    id: 'standup-pouch',
    name: 'Stand-up pouch',
    description: 'Mylar pouch with centered label',
    container: {
      containerType: 'STANDUP_POUCH',
      labelPlacement: 'FRONT_STICKER',
      labelFamily: 'RECT_STICKER',
      containerColor: '#cbd5e1',
      containerAccentColor: '#475569',
      lidColor: '#f1f5f9',
      fillOpacity: 0.85,
    },
    widthIn: 3,
    heightIn: 4,
    layoutTemplate: 'SQUARE_CENTER',
  },
  {
    id: 'jar-wrap',
    name: 'Jar · wrap label',
    description: 'Plastic jar with full wrap compliance label',
    container: {
      containerType: 'PLASTIC_JAR',
      labelPlacement: 'WRAP_AROUND',
      labelFamily: 'SLEEVE',
      containerColor: '#f8fafc',
      containerAccentColor: '#0f172a',
      lidColor: '#ffffff',
      fillOpacity: 0.3,
    },
    widthIn: 6,
    heightIn: 2.5,
    layoutTemplate: 'HORIZONTAL_SPLIT',
  },
  {
    id: 'bottle-sleeve',
    name: 'Bottle · sleeve',
    description: 'Dark bottle with premium wrap label',
    container: {
      containerType: 'BOTTLE',
      labelPlacement: 'WRAP_AROUND',
      labelFamily: 'SLEEVE',
      containerColor: '#1e293b',
      containerAccentColor: '#d4a853',
      lidColor: '#ca8a04',
      fillOpacity: 0.9,
    },
    widthIn: 5,
    heightIn: 2,
    layoutTemplate: 'HORIZONTAL_SPLIT',
  },
  {
    id: 'foil-pouch',
    name: 'Foil pouch',
    description: 'Silver foil bag with vertical tag',
    container: {
      containerType: 'FOIL_POUCH',
      labelPlacement: 'FRONT_STICKER',
      labelFamily: 'VERTICAL_TAG',
      containerColor: '#94a3b8',
      containerAccentColor: '#e2e8f0',
      lidColor: '#cbd5e1',
      fillOpacity: 0.95,
    },
    widthIn: 1.75,
    heightIn: 4,
    layoutTemplate: 'VERTICAL_STACK',
  },
  {
    id: 'preprinted-bag',
    name: 'Pre-printed bag',
    description: 'Retail bag with nutrition back panel',
    container: {
      containerType: 'PREPRINTED_BAG',
      labelPlacement: 'BACK_PANEL',
      labelFamily: 'NUTRITION_PANEL',
      containerColor: '#166534',
      containerAccentColor: '#fef08a',
      lidColor: '#14532d',
      fillOpacity: 1,
    },
    widthIn: 3,
    heightIn: 4.5,
    layoutTemplate: 'COMPLIANCE_GRID',
  },
  {
    id: 'box-carton',
    name: 'Box / carton',
    description: 'Carton with front product sticker',
    container: {
      containerType: 'BOX',
      labelPlacement: 'FRONT_STICKER',
      labelFamily: 'RECT_STICKER',
      containerColor: '#fef3c7',
      containerAccentColor: '#b45309',
      lidColor: '#fde68a',
      fillOpacity: 1,
    },
    widthIn: 3,
    heightIn: 3,
    layoutTemplate: 'SQUARE_CENTER',
  },
  {
    id: 'tin-can-wrap',
    name: 'Tin can · wrap',
    description: 'Spice tin with full wrap label',
    container: {
      containerType: 'TIN_CAN',
      labelPlacement: 'WRAP_AROUND',
      labelFamily: 'SLEEVE',
      containerColor: '#d4d4d8',
      containerAccentColor: '#52525b',
      lidColor: '#a1a1aa',
      fillOpacity: 1,
    },
    widthIn: 4.5,
    heightIn: 1.75,
    layoutTemplate: 'HORIZONTAL_SPLIT',
  },
  {
    id: 'mesh-sack-tag',
    name: 'Mesh sack · hang tag',
    description: 'Burlap sack with vertical hang tag',
    container: {
      containerType: 'MESH_SACK',
      labelPlacement: 'HANG_TAG',
      labelFamily: 'VERTICAL_TAG',
      containerColor: '#d6c4a8',
      containerAccentColor: '#92400e',
      lidColor: '#fef3c7',
      fillOpacity: 0.75,
    },
    widthIn: 1.5,
    heightIn: 3.5,
    layoutTemplate: 'VERTICAL_STACK',
  },
  {
    id: 'mesh-sack-sticker',
    name: 'Mesh sack · front stamp',
    description: 'Produce sack with front sticker',
    container: {
      containerType: 'MESH_SACK',
      labelPlacement: 'FRONT_STICKER',
      labelFamily: 'RECT_STICKER',
      containerColor: '#e7d5b8',
      containerAccentColor: '#78350f',
      lidColor: '#f5f5f4',
      fillOpacity: 0.7,
    },
    widthIn: 2.5,
    heightIn: 2,
    layoutTemplate: 'SQUARE_CENTER',
  },
  {
    id: 'clamshell-produce',
    name: 'Clamshell · top label',
    description: 'Berry/tomato clamshell with lid sticker',
    container: {
      containerType: 'CLAMSHELL',
      labelPlacement: 'TOP_LID',
      labelFamily: 'RECT_STICKER',
      containerColor: '#ecfdf5',
      containerAccentColor: '#059669',
      lidColor: '#f0fdf4',
      fillOpacity: 0.35,
    },
    widthIn: 3,
    heightIn: 2,
    layoutTemplate: 'SQUARE_CENTER',
  },
  {
    id: 'canister-sleeve',
    name: 'Canister · sleeve',
    description: 'Tall canister with branded sleeve',
    container: {
      containerType: 'CANISTER',
      labelPlacement: 'WRAP_AROUND',
      labelFamily: 'SLEEVE',
      containerColor: '#fafafa',
      containerAccentColor: '#171717',
      lidColor: '#d4d4d4',
      fillOpacity: 0.9,
    },
    widthIn: 5,
    heightIn: 3,
    layoutTemplate: 'HORIZONTAL_SPLIT',
  },
];

export function labelFamilyToLayoutTemplate(family: LabelFamily): LabelLayoutTemplate {
  const opt = LABEL_FAMILY_OPTIONS.find((o) => o.value === family);
  return opt?.layoutTemplate ?? 'SQUARE_CENTER';
}
